import { useEffect, useMemo } from 'react';
import { createInstance, Identify, Types } from '@amplitude/analytics-browser';
import { getUserType, useSession } from '@distribusion/portal-lib/auth';
import { PartnerTypeEnum } from '@distribusion/portal-lib/constants';
import { Partner } from '@distribusion/portal-lib/types';
import { ParticularPermissionAccess, useGetPermissionsDetails } from 'api/permissions';
import config from 'config';
import uniqBy from 'lodash/unionBy';

const packageJson = require('../../package.json');

const amplitude = createInstance();

if (config.amplitude.apiKey) {
  amplitude.init(config.amplitude.apiKey, undefined, {
    logLevel: Types.LogLevel.Warn,
    appVersion: packageJson.version,
    plan: {
      source: packageJson.name,
      branch: 'main',
    },
  });
}

export const useSetAmplitudeUser = () => {
  const { data: session, ...sessionQuery } = useSession();
  const { data: accessMap, ...permissionsQuery } = useGetPermissionsDetails({
    enabled: sessionQuery.isSuccess,
  });

  const email = useMemo(() => session?.identity?.traits?.email, [session?.identity?.traits?.email]);
  const accessType = useMemo(() => session && getUserType(session), [session]);
  const partners = useMemo(() => {
    if (accessMap) {
      const extractPartners = (access: ParticularPermissionAccess) =>
        Object.values(access.particular);
      const allPartners = Object.values(accessMap).reduce(
        (acc, { carriers, retailers }) =>
          acc.concat(extractPartners(carriers), extractPartners(retailers)),
        [] as Partner[],
      );
      return uniqBy(allPartners, 'code');
    }
    return [];
  }, [accessMap]);

  useEffect(() => {
    if (sessionQuery.isSuccess && email) {
      amplitude.setUserId(email);
    }
  }, [email, sessionQuery.isSuccess]);

  useEffect(() => {
    if (sessionQuery.isSuccess && permissionsQuery.isFetched && accessType) {
      const identifyEvent = new Identify();

      if (partners.length > 0) {
        identifyEvent.set(
          'carrier',
          partners.some(({ type }) => type === PartnerTypeEnum.CARRIER),
        );
        identifyEvent.set(
          'retailer',
          partners.some(({ type }) => type === PartnerTypeEnum.RETAILER),
        );
        identifyEvent.set(
          'partners',
          partners.map(({ code, name }) => `${code} ${name}`),
        );
      }

      amplitude.identify(identifyEvent);
    }
  }, [sessionQuery.isSuccess, permissionsQuery.isFetched, accessType, partners]);
};

export enum AmplitudeEventEnum {
  CREATE_USER_ACTION = 'create-user-action',
  VIEW_DOCS = 'view-docs',
  VIEW_LOGIN_PAGE = 'view-login-page',
  APPLY_USER_LIST_FILTERS = 'apply-user-list-filters',
  EDIT_USER_CLICK = 'edit-user-click',
  RECOVER_USER_ACTION = 'recover-user-action',
  CHANGE_USER_PASSWORD_ACTION = 'change-user-password-action',
  DEACTIVATE_USER_ACTION = 'deactivate-user-action',
  ACTIVATE_USER_ACTION = 'activate-user-action',
  VIEW_SERVICE_DESK = 'view-service-desk',
}

export { amplitude };
